/**
 * Which role does the Users table show, when a person holds one in two places?
 *
 * This platform has two role systems and they can disagree. `users.role_id` is the
 * legacy single role, and `user_roles` carries per-tenant assignments. Measured live
 * 2026-08-13, both users in the one account with sub-accounts enabled disagreed with
 * themselves, in OPPOSITE directions:
 *
 *   carlos+trippertenant@   users.role_id = Admin          user_roles here = Agency Admin
 *   aglasier24@             users.role_id = Agency Admin   user_roles here = Superadmin
 *
 * The table rendered `users.role_id` for both, so it understated one person's
 * authority and misstated the other's. An Agency Admin looked like an ordinary
 * Admin, which is exactly the confusion that made SUB-3.1's entry point look broken:
 * the gate correctly admitted an Agency Admin the screen was calling an Admin.
 *
 * ---------------------------------------------------------------------------
 * THE RULE, WHICH SUB-2.6 ALREADY WROTE DOWN
 * ---------------------------------------------------------------------------
 * `useUsers.ts:102` says of parent-granted users: "The role shown is the one they
 * hold HERE, not their role at the parent." That is the right principle and it was
 * applied to one narrow case. Applied consistently:
 *
 *   the role shown for the account being viewed is the role held IN that account,
 *   and the legacy column is the fallback for people who have no row there.
 *
 * ⚠️ ALL of them, comma-joined, NOT the "highest". One live user holds THREE rows in
 * a single tenant -- Admin, Sales Rep and Superadmin -- and this codebase defines no
 * precedence between roles anywhere. Picking one would be inventing a hierarchy, and
 * picking wrongly would hide a Superadmin behind the word "Sales Rep". Showing all
 * three is honest and also makes the data-hygiene problem visible, which a single
 * label would bury.
 *
 * ⚠️ NOT KEYED ON ROLE NAMES. Nothing here tests for 'Admin' or 'Agency Admin' or
 * any other spelling. It reports what is there. Keying a display on role names is
 * how `check_user_role` became the fourth spelling of "is this user an admin" and
 * how F-8 hid behind it, so even in a label this stays name-agnostic.
 */

export interface EffectiveRoleInput {
  /**
   * Names of the roles this person holds in the account being viewed, from
   * `user_roles`. Empty when they hold none there, which is the ordinary case for a
   * user whose access comes from the legacy column alone.
   */
  rolesInThisTenant?: (string | null | undefined)[] | null;
  /** The resolved name of `users.role_id`, or null when it is unset or unreadable. */
  legacyRoleName?: string | null;
}

export interface EffectiveRole {
  /** What to render. Never empty: falls back to a dash so a cell is never blank. */
  label: string;
  /**
   * The winning source's role names, unjoined, so a caller with its own per-name
   * formatter can apply it to each one.
   *
   * ⚠️ THIS EXISTS BECAUSE `label` CANNOT BE FORMATTED AS A WHOLE. `UserList.tsx`
   * passes role names through a `formatRole` helper that title-cases by
   * `charAt(0).toUpperCase() + slice(1).toLowerCase()`, so handing it the joined
   * label "Admin, Sales Rep, Superadmin" returns "Admin, sales rep, superadmin".
   * Format each element of this array and join the results.
   */
  names: string[];
  /**
   * Where the label came from, so a caller can badge or explain it.
   *
   * `tenant` means at least one `user_roles` row in this account. `legacy` means the
   * `users.role_id` column. `none` means neither, which renders as a dash and is a
   * real state: a parent-granted user's legacy `role_id` can be null.
   */
  source: 'tenant' | 'legacy' | 'none';
  /**
   * True when both sources have a value and they do not agree. The Users table does
   * not act on it today; it exists because "these two disagree" is the fact that
   * took a day to find, and a caller should be able to surface it without
   * recomputing the comparison.
   */
  disagrees: boolean;
}

const DASH = '-';

/** Trim, drop blanks and nulls, de-duplicate, and sort for a stable label. */
function clean(names: (string | null | undefined)[] | null | undefined): string[] {
  if (!names) return [];
  const seen = new Set<string>();
  for (const raw of names) {
    if (typeof raw !== 'string') continue;
    const name = raw.trim();
    if (name === '') continue;
    seen.add(name);
  }
  // Sorted so the label is stable across query orderings. Plain sort, not
  // localeCompare: this is a set of identifiers being made deterministic, not text
  // being presented in a user's locale, and localeCompare disagreeing with the
  // database collation is a trap this codebase has already been bitten by twice.
  return Array.from(seen).sort();
}

export function effectiveRole(input: EffectiveRoleInput): EffectiveRole {
  const here = clean(input.rolesInThisTenant);
  const legacy =
    typeof input.legacyRoleName === 'string' && input.legacyRoleName.trim() !== ''
      ? input.legacyRoleName.trim()
      : null;

  const disagrees =
    here.length > 0 && legacy !== null && !(here.length === 1 && here[0] === legacy);

  if (here.length > 0) {
    return { label: here.join(', '), names: here, source: 'tenant', disagrees };
  }
  if (legacy !== null) {
    return { label: legacy, names: [legacy], source: 'legacy', disagrees: false };
  }
  return { label: DASH, names: [], source: 'none', disagrees: false };
}

/** The label alone, for a render site or a search filter that needs one string. */
export function effectiveRoleLabel(input: EffectiveRoleInput): string {
  return effectiveRole(input).label;
}

/**
 * The same rule, answered as an ID, for the EDIT FORM rather than the table.
 *
 * WHY THIS EXISTS (USR-1.3). `UserForm.tsx` seeded its role select from
 * `initialData.role_id`, the legacy column, while the table beside it rendered
 * `user_roles` through `effectiveRole()` above. When those disagreed the form showed
 * one role and the table another, and once `update-admin-user` gained Step 5b that
 * mismatch stopped being cosmetic: saving an unrelated field would write the form's
 * stale value into BOTH systems. Seven accounts were in that state on 2026-08-14 and
 * had to be reconciled by `20260814g` before the write path could ship safely.
 *
 * The form must therefore seed from the same source the table renders. Same rule,
 * different return type: ids, because a form submits an id.
 *
 * ⚠️ IT REFUSES TO CHOOSE WHEN THERE IS MORE THAN ONE, exactly as `effectiveRole`
 * refuses to pick a "highest" for the label. A single select cannot represent three
 * roles, and `planTenantRoleWrite` deletes every own-role that is not the one
 * submitted, so silently seeding the first would make an admin drop two roles by
 * opening a form and pressing Save. `ambiguous` says so and `roleId` stays null, which
 * leaves the caller to force a deliberate choice rather than make a quiet wrong one.
 * One account is in this state live and it is soft-deleted; see USR-1.4.
 */
export interface EffectiveRoleIdInput {
  /** Role ids held in the account being viewed, from `user_roles`. */
  roleIdsInThisTenant?: (number | string | null | undefined)[] | null;
  /** The legacy `users.role_id`, or null when unset. */
  legacyRoleId?: number | string | null;
}

export interface EffectiveRoleId {
  /** The id a form should seed with, or null when there is nothing safe to seed. */
  roleId: number | null;
  /** Every id held in the account being viewed, cleaned and sorted. */
  ids: number[];
  /** Which source answered. Mirrors `EffectiveRole['source']`. */
  source: 'tenant' | 'legacy' | 'none';
  /** True when this account holds more than one role, so no single id represents it. */
  ambiguous: boolean;
}

/** Coerce to a positive integer id, or null. Ids arrive as strings over PostgREST. */
function toId(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function effectiveRoleId(input: EffectiveRoleIdInput): EffectiveRoleId {
  const seen = new Set<number>();
  for (const raw of input.roleIdsInThisTenant ?? []) {
    const id = toId(raw);
    if (id !== null) seen.add(id);
  }
  const ids = Array.from(seen).sort((a, b) => a - b);

  if (ids.length === 1) {
    return { roleId: ids[0], ids, source: 'tenant', ambiguous: false };
  }
  if (ids.length > 1) {
    // Held here, but not expressible in one select. Say so rather than pick.
    return { roleId: null, ids, source: 'tenant', ambiguous: true };
  }

  const legacy = toId(input.legacyRoleId);
  if (legacy !== null) {
    return { roleId: legacy, ids: [], source: 'legacy', ambiguous: false };
  }
  return { roleId: null, ids: [], source: 'none', ambiguous: false };
}
